import _ from "lodash";
import { useCallback } from "react";
import { Sentry } from "services/sentry";
import { INSTRUCTION_REGEX } from "../modules/AiQuestionLoaderConstant";
import { updateQuestionOrder } from "Worksheet/modules/WorksheetModules";
import {
  isWorksheetQueriesV2Enabled,
  isWorksheetMutationsV2Enabled,
} from "Worksheet/modules/WorksheetFeatureFlags";
import {
  convertMdToHTML,
  replaceLatexWithImages,
  getNonCachedLastQuestionId,
  generateQuestionSequenceMap,
  getNonCachedLastQuestionIdV2,
  generateQuestionSequenceMapV2,
} from "../modules/AiQuestionLoaderUtils";

const ORG_RESOURCES = { type: "WORKSHEET_RESOURCE", resourceType: "QUESTION" };

// * Returns last question id from sections
const getLastQuestionOrNodeId = sections => {
  const lastSection = _.last(sections);
  const isQueriesV2Enabled = isWorksheetQueriesV2Enabled();
  const { lastNodeId = null, lastQuestionId = null } = isQueriesV2Enabled
    ? getNonCachedLastQuestionIdV2(lastSection)
    : getNonCachedLastQuestionId(lastSection);
  return { lastNodeId, lastQuestionId };
};

// * Sanitizes instructionText <ul> and <ol> blocks by removing unnecessary whitespace and <br> tags.
const getSanitizedWorksheetInstructionText = async ({
  uploadFileV2,
  instructionText,
}) => {
  const { RGX1, RGX2, RGX3, RGX4 } = INSTRUCTION_REGEX;
  let instruction = convertMdToHTML(instructionText, false);
  instruction = await replaceLatexWithImages(instruction, uploadFileV2);
  return _.replace(instruction, RGX1, block =>
    block.replace(RGX2, "</li><li").replace(RGX3, "$1").replace(RGX4, "$1")
  );
};

/**
 * Custom hook that provides functionality to add AI-generated questions to a worksheet.
 * Handles both V1 and V2 versions of the API based on feature flag configuration.
 *
 * @param {Object} props - Configuration object
 * @returns {Function} Async function to add questions to worksheet
 */
export const useAddAiQuestionsToWorksheet = props => {
  const {
    gradeId,
    sections,
    questionId,
    parentItemId,
    usageMetricsId,
    parentResourceId,
    questionResourceId,
    worksheetMutationData,
    createWorksheetQuestionAI,
    isAddingQuestionsInBetween,
    createWorksheetQuestionAIV2,
  } = props;

  /**
   * Creates a question using the V1 API endpoint.
   * @V1 uses a legacy organizationResource structure for managing resource hierarchy.
   */
  const createQuestionV1 = useCallback(
    async ({ offset, aiSequence, questionContent }) => {
      const { parentId, parentType } = worksheetMutationData;
      const lastSectionId = _.get(_.last(sections), "id", null);
      if (_.isEmpty(parentId) || !lastSectionId) return null;
      const { lastNodeId } = getLastQuestionOrNodeId(sections);

      const payload = {
        input: {
          offset,
          gradeId,
          aiSequence,
          usageMetricsId,
          questionContent,
          organizationResource: {
            parentId,
            parentType,
            shouldHandleSequence: true,
            organizationResources: ORG_RESOURCES,
            parentResourceId: parentResourceId || lastSectionId,
            putAfterItemId: isAddingQuestionsInBetween
              ? questionResourceId
              : lastNodeId,
          },
        },
        parentId,
        parentType,
        refetch: false,
      };
      return createWorksheetQuestionAI(payload);
    },
    [
      gradeId,
      sections,
      usageMetricsId,
      parentResourceId,
      questionResourceId,
      createWorksheetQuestionAI,
      isAddingQuestionsInBetween,
    ]
  );

  /**
   * Creates a question using the V2 API endpoint.
   * @V2 uses a simplified input structure with worksheetId and hierarchical parent reference.
   */
  const createQuestionV2 = useCallback(
    async ({ offset, aiSequence, questionContent }) => {
      const { parentId } = worksheetMutationData;
      const isQueriesV2Enabled = isWorksheetQueriesV2Enabled();
      const lastSectionId = _.get(_.last(sections), "item.id", null);
      if (_.isEmpty(parentId) || !lastSectionId) return null;
      const { lastQuestionId } = getLastQuestionOrNodeId(sections);

      const payload = {
        input: {
          offset,
          gradeId,
          aiSequence,
          usageMetricsId,
          questionContent,
          worksheetId: parentId,
          parentType: "QUESTION",
          parentId: parentItemId || lastSectionId,
          putAfterQuestionId: questionId || lastQuestionId,
        },
        refetch: false,
        isQueriesV2Enabled,
      };

      return createWorksheetQuestionAIV2(payload);
    },
    [
      gradeId,
      sections,
      questionId,
      parentItemId,
      usageMetricsId,
      worksheetMutationData,
      createWorksheetQuestionAIV2,
    ]
  );

  /**
   * Adds an AI-generated question to the worksheet.
   * Prepares the payload with necessary metadata and invokes the appropriate
   * mutation function based on the enabled API version.
   */
  return useCallback(
    async payload => {
      try {
        // * Determine which API version to use based on feature flag configuration
        const isMutationsV2Enabled = isWorksheetMutationsV2Enabled();
        const createQuestion = isMutationsV2Enabled
          ? createQuestionV2
          : createQuestionV1;

        // Execute the mutation and extract the created question ID
        const createdQuestion = await createQuestion(payload);
        const v1MutationKey = "createWorksheetQuestionsUsingAI";
        const v2MutationKey = "createWorksheetQuestionsUsingAiV2";
        const qKey = isMutationsV2Enabled ? v2MutationKey : v1MutationKey;
        return _.get(createdQuestion, ["data", "content", qKey, "0", "id"], "");
      } catch {
        // Return "" on mutation failure to allow graceful error handling
        return "";
      }
    },
    [sections, createQuestionV1, createQuestionV2, worksheetMutationData]
  );
};

/**
 * Custom hook that manages the reordering of AI-generated questions within a worksheet.
 * Handles both V1 and V2 versions of the API based on feature flag configuration.
 *
 * @param {Object} props - Configuration object
 * @returns {Function} Async function to update question order with sequence mapping
 */
export const useUpdateAiGeneratedQuestionsOrder = props => {
  const isQueriesV2Enabled = isWorksheetQueriesV2Enabled();
  const { sections, worksheetMutationData, getAssessmentQuestions } = props;

  // Select the appropriate sequence generation function based on API version
  const generateQuestionSequence = isQueriesV2Enabled
    ? generateQuestionSequenceMapV2
    : generateQuestionSequenceMap;

  // * Updates the order of AI-generated questions in the worksheet.
  return useCallback(
    async sequenceToQuestionId => {
      const { parentId } = worksheetMutationData;
      if (_.isEmpty(parentId)) return null;

      // Generate sequence mapping based on API version and data source
      const questionSequence = generateQuestionSequence(
        isQueriesV2Enabled ? sections : getAssessmentQuestions,
        sequenceToQuestionId
      );

      if (_.isEmpty(questionSequence)) return null;

      try {
        await updateQuestionOrder(parentId, questionSequence);
      } catch (error) {
        // Log error to Sentry for monitoring and debugging
        Sentry.captureMessage(`AI Question order update failed`, {
          level: "error",
          tags: {
            component: "AiQuestionLoader",
          },
          extra: {
            functionName: "updateQuestionOrder",
            error: error?.message,
            sections,
            parentId,
            questionSequence,
          },
        });
        return null;
      }
    },
    [
      sections,
      isQueriesV2Enabled,
      worksheetMutationData,
      getAssessmentQuestions,
      generateQuestionSequence,
    ]
  );
};

/**
 * Custom hook that orchestrates the saving of AI-generated questions to the server.
 * Monitors the streaming state of questions and triggers mutations to save completed
 *
 * @param {*} props - Configuration object
 * @returns {Function} Async function to save worksheet instruction
 */
export const useUpdateAiGeneratedWsInstruction = props => {
  const {
    uploadFileV2,
    descriptionField,
    updateAssessment,
    updateAssessmentInCache,
  } = props;

  // Helper function to construct the payload for updating the worksheet instruction
  const getAssessmentPayload = _value => {
    const id = _.get(descriptionField, "id", "");
    const uid = _.get(descriptionField, "uid", "");
    const value = _value || "Generating worksheet instruction...";
    const params = { id, uid, value };
    return {
      params,
      fieldUID: uid,
      fieldObj: {
        ...params,
        __typename: "PlannerField",
        resolvedMinimalTree: {
          id,
          type: "UNDEFINED_ENTITY",
          __typename: "ResolvedFieldUndefinedEntity",
        },
      },
      extraParams: {},
    };
  };

  // * Update worksheet instruction in cache and server both
  const updateWorksheetInstruction = useCallback(
    async instructionText => {
      updateAssessmentInCache(getAssessmentPayload());
      const updatedInstructionText = await getSanitizedWorksheetInstructionText(
        {
          uploadFileV2,
          instructionText,
        }
      );
      updateAssessmentInCache(getAssessmentPayload(updatedInstructionText));

      await updateAssessment({
        hasAddRemoveValues: false,
        value: updatedInstructionText,
        id: _.get(descriptionField, "id", ""),
      });
    },
    [descriptionField, updateAssessment, updateAssessmentInCache, uploadFileV2]
  );

  return updateWorksheetInstruction;
};
